import { Activity } from '@valuecase/common'
import { DateTime } from 'luxon'
import { ReactNode, useCallback, useMemo } from 'react'
import { useDateFormatting } from '../../utils'

type ActivityTextProps = {
  activity: Activity
  pageName: string | ReactNode
  fileName: string | ReactNode
  blockName: string | ReactNode
  taskName: string | ReactNode
  assigneeName: string | ReactNode
  dueDate: string
  action: string | ReactNode
  milestoneName: string | ReactNode
  metadata: any
  listItemTitle: string | ReactNode
  spaceName?: string | ReactNode // Optional for backwards compatibility
  link: string | ReactNode
  daysOffset: number
  formTitle: string | ReactNode
  questionText: string | ReactNode
  answer: string | ReactNode
}

interface ActivityTextRenderProps {
  activity: Activity
  onShowPageOrBlock: (e: React.MouseEvent<HTMLElement>, activity: Activity) => void
  onShowActivityTaskDetails: (activity: Activity) => void
  getBlockTitle: (data: any) => string | undefined
  metadata: {
    milestoneName?: string
    taskName?: string
    assigneeName?: string
    dueDate?: string
    action?: string
    quickActionName?: string
    listItemTitle?: string
    fileName?: string
    link?: string
    daysOffset?: number
    formTitle?: string
    questionText?: string
    answer?: string
    [key: string]: any
  } & Record<string, any>
  page: Record<string, any>
}

export function ActivityTextRender({
  activity,
  onShowPageOrBlock,
  onShowActivityTaskDetails,
  getBlockTitle,
  metadata,
  page,
}: ActivityTextRenderProps) {
  const { getActivityText } = useActivityText()

  const milestoneName = metadata?.milestoneName || metadata?.taskName || 'Unknown milestone'
  const assigneeName = metadata?.assigneeName || 'Unknown asignee'
  const dueDate = metadata?.dueDate || 'Unknown due date'
  const action = metadata?.action || metadata?.quickActionName || 'Unknown action'

  const listItemTitle = useMemo(() => {
    return (
      <a
        role='button'
        className={'text-primary-s5'}
        onClick={(e) => onShowPageOrBlock(e, activity)}
      >
        {metadata?.listItemTitle || 'Unknown accordion item'}
      </a>
    )
  }, [metadata?.listItemTitle, onShowPageOrBlock, activity])

  const fileName = useMemo(() => {
    return (
      <a
        role='button'
        className={'contents text-primary-s5'}
        onClick={(e) => onShowPageOrBlock(e, activity)}
      >
        {
          (metadata?.fileName ||
            activity.node?.data?.fileName ||
            activity.node?.data?.linkName ||
            'Unknown file') as string
        }
      </a>
    )
  }, [activity, metadata, onShowPageOrBlock])

  const pageName = useMemo(() => {
    const name =
      page?.versions?.[0]?.asset?.data?.title || activity.node?.data?.title || 'Unknown page'
    return (
      <a
        role='button'
        className={'contents text-primary-s5'}
        onClick={(e) => onShowPageOrBlock(e, activity)}
      >
        {name}
      </a>
    )
  }, [activity, onShowPageOrBlock, page])

  const blockName = useMemo(() => {
    return (
      <a
        role='button'
        className={'contents text-primary-s5'}
        onClick={(e) => onShowPageOrBlock(e, activity)}
      >
        {getBlockTitle(activity) || 'Unknown block'}{' '}
      </a>
    )
  }, [activity, getBlockTitle, onShowPageOrBlock])

  const taskName = useMemo(() => {
    return (
      <a
        role='button'
        className={'contents text-primary-s5'}
        onClick={() => onShowActivityTaskDetails(activity)}
      >
        {metadata?.taskName || metadata?.taskTitle || 'Unknown task'}
      </a>
    )
  }, [activity, onShowActivityTaskDetails, metadata?.taskName, metadata?.taskTitle])

  const link = useMemo(() => {
    return (
      <a role='button' className={'contents text-primary-s5'} href={metadata?.link}>
        {metadata?.link || 'Unknown link'}
      </a>
    )
  }, [metadata?.link])

  const daysOffset = useMemo(() => {
    return metadata?.daysOffset ?? 0
  }, [metadata?.daysOffset])

  const formTitle = useMemo(() => {
    return (
      <a
        role='button'
        className={'contents text-primary-s5'}
        onClick={(e) => onShowPageOrBlock(e, activity)}
      >
        {metadata?.formTitle || 'Unknown form question'}
      </a>
    )
  }, [activity, metadata?.formTitle, onShowPageOrBlock])

  const questionText = useMemo(() => {
    return (
      <a
        role='button'
        className={'contents text-primary-s5'}
        onClick={(e) => onShowPageOrBlock(e, activity)}
      >
        {metadata?.questionText || 'Unknown form question'}
      </a>
    )
  }, [activity, metadata?.questionText, onShowPageOrBlock])

  const answer = useMemo(() => {
    return (
      <span className='text-grey-s5 font-medium italic'>
        {metadata?.answer || 'Unknown answer'}
      </span>
    )
  }, [metadata?.answer])

  const activityText = useMemo(
    () =>
      getActivityText({
        activity,
        pageName,
        fileName,
        blockName,
        taskName,
        assigneeName,
        dueDate,
        action,
        milestoneName,
        metadata,
        listItemTitle,
        link,
        daysOffset,
        formTitle,
        questionText,
        answer,
      }),
    [
      getActivityText,
      activity,
      pageName,
      fileName,
      blockName,
      taskName,
      assigneeName,
      dueDate,
      action,
      milestoneName,
      metadata,
      listItemTitle,
      link,
      daysOffset,
      formTitle,
      questionText,
      answer,
    ],
  )

  return activityText
}

export const useActivityText = () => {
  const { formatDateShort } = useDateFormatting()

  const getActivityText = useCallback(
    ({
      activity,
      pageName,
      fileName,
      blockName,
      taskName,
      assigneeName,
      dueDate,
      action,
      milestoneName,
      metadata,
      listItemTitle,
      spaceName,
      link,
      daysOffset,
      formTitle,
      questionText,
      answer,
    }: ActivityTextProps) => {
      switch (activity.type) {
        case 'VIEW':
          return <span>visited the page {pageName}</span>
        case 'ATTACHMENT_LINK_COPIED':
          return (
            <>
              copied the link to the file {fileName} on page {pageName}
            </>
          )
        case 'ATTACHMENT_PREVIEW':
          return (
            <>
              opened the preview for file {fileName} on page {pageName}
            </>
          )
        case 'ATTACHMENT_DOWNLOAD':
          return (
            <>
              downloaded the file {fileName} on page {pageName}
            </>
          )
        case 'DIRECT_LINK_TO_FILE_CLICKED':
          return <>a direct link to the file {fileName} was clicked</>
        case 'ATTACHMENT_PREVIEW_FULLSCREEN':
          return (
            <>
              opened the file {fileName} in the block {blockName} in full screen mode
            </>
          )
        case 'MAP_MILESTONE_TASK_DETAILS_OPENED':
          return (
            <>
              opened the task detail card of the task {taskName} on page {pageName}
            </>
          )
        case 'MAP_MILESTONE_TASK_COMPLETED':
          return (
            <>
              completed the task {taskName} on page {pageName}
            </>
          )
        case 'MAP_MILESTONE_TASK_ADDED':
          return (
            <>
              added the task {taskName} on page {pageName}
            </>
          )
        case 'MAP_MILESTONE_TASK_CHANGED':
          return (
            <>
              changed a task name to {taskName} on page {pageName}
            </>
          )
        case 'MAP_MILESTONE_TASK_DELETED':
          return (
            <>
              deleted the task {taskName} on page {pageName}
            </>
          )

        case 'MAP_MILESTONE_TASK_ASSIGNEE_ADDED':
          return (
            <>
              assigned {assigneeName} to the task {taskName}
            </>
          )
        case 'MAP_MILESTONE_TASK_ASSIGNEE_DELETED':
          return (
            <>
              deleted the assignee {assigneeName} from task {taskName}
            </>
          )
        case 'MAP_MILESTONE_TASK_DUE_DATE_ADDED':
          return (
            <>
              set the due date {formatDateShort(DateTime.fromISO(dueDate, { zone: 'utc' }))} for
              task {taskName} on page {pageName}
            </>
          )
        case 'MAP_MILESTONE_TASK_DUE_DATE_DELETED':
          return (
            <>
              deleted the due date of task {taskName} on page {pageName}
            </>
          )
        case 'MAP_MILESTONE_TASK_DUE_DATE_CHANGED':
          return (
            <>
              changed the due date of the task {taskName} to{' '}
              {formatDateShort(DateTime.fromISO(dueDate, { zone: 'utc' }))} on page {pageName}
            </>
          )
        case 'MAP_MILESTONE_TASK_ATTACHMENT_UPLOADED':
          return (
            <>
              uploaded a file to the task {taskName} on page {pageName}
            </>
          )
        case 'MAP_MILESTONE_TASK_DOWNLOAD_FILE':
          return (
            <>
              downloaded the file {fileName} from the task {taskName}
            </>
          )
        case 'MAP_MILESTONE_TASK_DELETE_FILE':
          return (
            <>
              deleted the file {fileName} from the task {taskName}
            </>
          )
        case 'MAP_MILESTONE_TASK_QUICK_ACTION_CLICK':
          return (
            <>
              clicked on the quick action {action} of the task {taskName}
            </>
          )
        case 'MAP_MILESTONE_TASK_DESCRIPTION_ADDED':
          return (
            <>
              added a description to the task {taskName} on page {pageName}
            </>
          )
        case 'MAP_MILESTONE_TASK_DESCRIPTION_CHANGED':
          return (
            <>
              edited the description of the task {taskName} on page {pageName}
            </>
          )
        case 'MAP_MILESTONE_TASK_DESCRIPTION_DELETED':
          return (
            <>
              deleted the description of the task {taskName} on page {pageName}
            </>
          )
        case 'MAP_MILESTONE_ADDED':
          return (
            <>
              added the milestone {milestoneName} on page {pageName}
            </>
          )
        case 'MAP_MILESTONE_DELETED':
          return (
            <>
              deleted the milestone {milestoneName} on page {pageName}
            </>
          )
        case 'MAP_MILESTONE_COMPLETED':
          return (
            <>
              completed the milestone {milestoneName} on page {pageName}
            </>
          )
        case 'BUSINESS_CASE_RECALCULATED':
          return <>recalculated the calculator results on page {pageName}</>
        case 'SPACE_LINK_COPIED':
          return <>copied the invite link</>
        case 'PDF_CONTENT_VIEW':
          return (
            <>
              read pages 1 to {metadata.maxPageRead} of the document {metadata.fileName} in the
              block {blockName}
            </>
          )
        case 'TESTIMONIAL_VIEW':
          return (
            <>
              interacted {metadata.interactions} times with the cards block {blockName}
            </>
          )
        case 'INLINE_VIDEO_PLAY':
          return (
            <>
              watched the video {metadata.fileName} in the block {blockName}
            </>
          )

        case 'BLOCK_INTRO_VIDEO_PLAY':
          return <>watched the block intro video of the block {blockName}</>
        case 'COLLABORATIVE_BLOCK_EDITED':
          return <>edited the text box {blockName}</>
        case 'INTERACTIVE_LIST_ITEM_OPENED':
          return (
            <>
              clicked on the accordion item {listItemTitle} on page {pageName}
            </>
          )
        case 'COMMENT':
          if (activity.node?.type === 'BLOCK_MAP_MILESTONE_TASK') {
            return (
              <>
                added a comment to the task {taskName} on page {pageName}
              </>
            )
          } else {
            return (
              <>
                wrote a comment on block {blockName} on page {pageName}
              </>
            )
          }
        case 'SPACE_COMMENT':
          return <>added a general comment to {spaceName ?? 'the space'}</>
        case 'BLOCK_LINK_COPIED':
          return (
            <>
              copied the link to block {blockName} on page {pageName}
            </>
          )
        case 'TASK_LINK_COPIED':
          return (
            <>
              copied the link to task {taskName} on page {pageName}
            </>
          )
        case 'BLOCK_FILE_UPLOADED':
          return (
            <>
              uploaded a file to block {blockName} on page {pageName}
            </>
          )
        case 'ARCHIVED_VIEW':
          return <>tried to access this archived space. Maybe a good opportunity to reconnect?</>
        case 'BUYER_NOTIFICATION_SENT_MILESTONE_DONE':
          return <>received a notification email for a completed milestone on block {blockName}</>
        case 'BUYER_NOTIFICATION_SENT_TASK_ASSIGNED':
          return (
            <>
              received a notification email because a task was assigned to them on block {blockName}
            </>
          )
        case 'BUYER_NOTIFICATION_SENT_COMMENT':
          return <>received a notification email for a comment on block {blockName}</>
        case 'BUYER_NOTIFICATION_SENT_SPACE_COMMENT':
          return (
            <>received a notification email for a general comment on {spaceName ?? 'the space'}</>
          )
        case 'BUYER_NOTIFICATION_SENT_TASK_DUE_TODAY':
          return <>received a notification email because one of their tasks is due today</>
        case 'BUYER_NOTIFICATION_SENT_TASK_DUE_X_DAYS_AGO':
          return (
            <>
              received a notification email because one of their tasks is overdue by {daysOffset}{' '}
              {daysOffset === 1 ? 'day' : 'days'}
            </>
          )

        case 'BUYER_NOTIFICATION_SENT_TASK_DUE_IN_X_DAYS':
          return (
            <>
              received a notification email because one of their tasks is due in{' '}
              {Math.abs(daysOffset)} {Math.abs(daysOffset) === 1 ? 'day' : 'days'}
            </>
          )
        case 'LINK_CLICKED':
          return (
            <>
              opened the link {link} on page {pageName}
            </>
          )
        case 'FORM_QUESTION_ANSWERED':
          return (
            <>
              responded {answer} to the question {questionText} on page {pageName}
            </>
          )
        case 'FORM_QUESTION_EDITED':
          return (
            <>
              edited the question {questionText} with the answer {answer} on page {pageName}
            </>
          )
        case 'FORM_COMPLETED':
          return (
            <>
              completed the form {formTitle} on page {pageName}
            </>
          )
        default:
          return <></>
      }
    },
    [formatDateShort],
  )

  return {
    getActivityText,
  }
}
